import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, Plus, Trash2, AlertCircle, Info } from 'lucide-react'
import { apiGet, postJson, apiDelete } from '../../lib/api.js'
import { POSITIONS } from '../../lib/employee.js'
import ConfirmDialog from '../ConfirmDialog.jsx'
import { RELATIONS, relationLabel, TONE_CLASS } from '../../lib/evaluation.js'

// เกณฑ์การประเมิน — the matrix. One row per (ตำแหน่งที่ถูกประเมิน × กลุ่มผู้ประเมิน):
// which questionnaire that group fills in, and how much their answers count.
//
// This screen IS the customisation the spec asks for. A back-office position is not a
// special case in the code — it is just a position with its own set of rows.
//
// Weights need not add to 100. The roll-up renormalises over whichever groups actually
// returned answers, so a manager who never fills theirs in cannot drag a score to zero;
// their share is redistributed instead. The bar under each position shows the nominal split
// so an unintentional 30/30 still reads as odd.

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'
const LABEL = 'block text-[12.5px] font-medium text-slate-500 mb-1'

export default function EvaluationRules() {
  const [rules, setRules] = useState([])
  const [forms, setForms] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [editing, setEditing] = useState(null)
  const [confirmDel, setConfirmDel] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const [r, f] = await Promise.all([
        apiGet('/api/hr/evaluation/rule'),
        apiGet('/api/hr/evaluation/form', { status: 'active' }),
      ])
      setRules(Array.isArray(r) ? r : [])
      setForms(Array.isArray(f) ? f : [])
    } catch (e) {
      setErr(e?.message || 'โหลดเกณฑ์ไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const formName = useMemo(() => {
    const m = new Map(forms.map((f) => [f.id, f.title]))
    return (id) => m.get(id) || `ชุดคำถาม #${id}`
  }, [forms])

  const byPosition = useMemo(() => {
    const m = new Map()
    for (const r of rules) {
      if (!m.has(r.subject_position)) m.set(r.subject_position, [])
      m.get(r.subject_position).push(r)
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], 'th'))
  }, [rules])

  async function remove(rule) {
    setConfirmDel(null)
    try {
      await apiDelete(`/api/hr/evaluation/rule/${rule.id}`)
      await load()
    } catch (e) {
      setErr(e?.message || 'ลบเกณฑ์ไม่สำเร็จ')
    }
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-4">
        <p className="text-[13px] text-slate-500 max-w-2xl">
          กำหนดว่าแต่ละตำแหน่งจะถูกประเมินโดยใคร ใช้ชุดคำถามไหน และคะแนนของแต่ละกลุ่มมีน้ำหนักเท่าไร
        </p>
        <button
          type="button"
          onClick={() => setEditing({ subject_position: '', relation: 'peer', form_id: 0, weight_percent: 0, min_evaluators: 1, peer_positions: [] })}
          className="px-4 py-2.5 rounded-xl text-[14px] font-semibold text-white bg-brand-600 hover:bg-brand-700 shadow-sm inline-flex items-center gap-2 flex-none"
        >
          <Plus size={15} strokeWidth={2} /> เพิ่มเกณฑ์
        </button>
      </div>

      {err && <div className="text-[13px] text-rose-500 mb-3">{err}</div>}
      {loading && (
        <div className="flex items-center gap-2 text-[13.5px] text-slate-400 py-10 justify-center">
          <Loader2 size={15} className="animate-spin" /> กำลังโหลด…
        </div>
      )}

      {!loading && !byPosition.length && (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-12 text-center">
          <p className="text-[14px] text-slate-500">ยังไม่ได้ตั้งเกณฑ์</p>
          <p className="text-[13px] text-slate-400 mt-1">
            ต้องมีเกณฑ์อย่างน้อยหนึ่งข้อต่อหนึ่งตำแหน่ง ไม่งั้นตำแหน่งนั้นจะเลือกเข้ารอบประเมินไม่ได้
          </p>
        </div>
      )}

      <div className="space-y-4">
        {byPosition.map(([position, list]) => {
          const total = list.reduce((s, r) => s + Number(r.weight_percent || 0), 0)
          return (
            <div key={position} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex items-center gap-3">
                <h3 className="text-[14px] font-semibold text-slate-800">{position}</h3>
                <span className={`inline-flex rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 ${
                  total === 100 ? TONE_CLASS.emerald : TONE_CLASS.amber
                }`}>
                  น้ำหนักรวม {total}%
                </span>
                {total !== 100 && (
                  <span className="text-[11.5px] text-slate-400 inline-flex items-center gap-1">
                    <Info size={11} /> ไม่ต้องรวมเป็น 100 ก็ได้ ระบบจะเฉลี่ยตามสัดส่วนที่มีจริง
                  </span>
                )}
              </div>

              <table className="w-full text-[13.5px]">
                <thead>
                  <tr className="text-[12px] font-medium text-slate-400 border-b border-slate-100">
                    <th className="text-left px-5 py-2">ประเมินโดย</th>
                    <th className="text-left px-4 py-2">ชุดคำถาม</th>
                    <th className="text-right px-4 py-2">น้ำหนัก</th>
                    <th className="text-right px-4 py-2">ขั้นต่ำ</th>
                    <th className="text-left px-4 py-2">ตำแหน่งที่นับเป็นเพื่อนร่วมงาน</th>
                    <th className="px-3" />
                  </tr>
                </thead>
                <tbody>
                  {list.map((r) => (
                    <tr key={r.id} className="border-b border-slate-50 last:border-b-0 hover:bg-brand-50/30">
                      <td className="px-5 py-2.5 text-slate-800">{relationLabel(r.relation)}</td>
                      <td className="px-4 py-2.5">
                        <button
                          type="button"
                          className="text-brand-700 hover:underline text-left"
                          onClick={() => setEditing({ ...r })}
                        >
                          {formName(r.form_id)}
                        </button>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">{r.weight_percent}%</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{r.min_evaluators}</td>
                      <td className="px-4 py-2.5 text-[12.5px] text-slate-500">
                        {r.relation === 'peer' ? ((r.peer_positions || []).join(', ') || 'ทุกตำแหน่งในสาขา') : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <button
                          type="button"
                          onClick={() => setConfirmDel(r)}
                          className="p-1.5 rounded-lg text-slate-300 hover:text-rose-600 hover:bg-rose-50"
                          title="ลบเกณฑ์นี้"
                        >
                          <Trash2 size={14} strokeWidth={1.75} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        })}
      </div>

      {editing && (
        <RuleModal
          rule={editing}
          forms={forms}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load() }}
        />
      )}

      {confirmDel && (
        <ConfirmDialog
          title="ลบเกณฑ์นี้?"
          message={`${confirmDel.subject_position} · ${relationLabel(confirmDel.relation)} — แบบประเมินที่ส่งไปแล้วจะยังคงอยู่และยังคิดคะแนนตามน้ำหนักเดิม`}
          confirmLabel="ลบ"
          tone="danger"
          onConfirm={() => remove(confirmDel)}
          onCancel={() => setConfirmDel(null)}
        />
      )}
    </div>
  )
}

function RuleModal({ rule, forms, onClose, onSaved }) {
  const [v, setV] = useState(rule)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const set = (patch) => setV((p) => ({ ...p, ...patch }))

  // Only offer question sets that declare themselves usable for this (position, relation).
  // An empty declaration on the form means "any", so forms authored before those fields
  // existed stay selectable everywhere rather than vanishing from the picker.
  const matching = useMemo(() => forms.filter((f) => {
    const pos = f.subject_positions || []
    const rel = f.evaluator_relations || []
    return (!pos.length || !v.subject_position || pos.includes(v.subject_position))
      && (!rel.length || rel.includes(v.relation))
  }), [forms, v.subject_position, v.relation])

  const hidden = forms.length - matching.length

  async function save() {
    setBusy(true)
    setErr('')
    try {
      // POST upserts on (subject_position, relation) — no separate create/update path.
      await postJson('/api/hr/evaluation/rule', {
        subject_position: v.subject_position,
        relation: v.relation,
        form_id: Number(v.form_id),
        weight_percent: Number(v.weight_percent || 0),
        min_evaluators: Number(v.min_evaluators || 1),
        peer_positions: v.relation === 'peer' ? (v.peer_positions || []) : [],
      })
      onSaved()
    } catch (e) {
      setErr(e?.message || 'บันทึกไม่สำเร็จ')
      setBusy(false)
    }
  }

  const togglePeer = (p) => {
    const cur = new Set(v.peer_positions || [])
    if (cur.has(p)) cur.delete(p); else cur.add(p)
    set({ peer_positions: [...cur] })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl animate-ss-pop-in max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="text-[16px] font-semibold text-slate-900">{rule.id ? 'แก้ไขเกณฑ์' : 'เพิ่มเกณฑ์'}</h3>
        </div>
        <div className="px-6 py-5 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>ตำแหน่งที่ถูกประเมิน</label>
              <select className={FIELD} value={v.subject_position} onChange={(e) => set({ subject_position: e.target.value })}>
                <option value="">— เลือก —</option>
                {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>ประเมินโดย</label>
              <select className={FIELD} value={v.relation} onChange={(e) => set({ relation: e.target.value })}>
                {RELATIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className={LABEL}>ชุดคำถามที่ใช้</label>
            <select className={FIELD} value={v.form_id} onChange={(e) => set({ form_id: e.target.value })}>
              <option value={0}>— เลือก —</option>
              {matching.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
            </select>
            <p className="text-[11.5px] text-slate-400 mt-1">
              แสดงเฉพาะชุดคำถามที่สถานะเป็น active และประกาศว่าใช้กับตำแหน่ง/กลุ่มนี้ได้
              {hidden > 0 && ` · ซ่อนอยู่ ${hidden} ชุด`}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>น้ำหนักคะแนน (%)</label>
              <input type="number" min={0} max={100} className={FIELD} value={v.weight_percent}
                onChange={(e) => set({ weight_percent: e.target.value })} />
            </div>
            <div>
              <label className={LABEL}>จำนวนผู้ประเมินขั้นต่ำ</label>
              <input type="number" min={1} className={FIELD} value={v.min_evaluators}
                onChange={(e) => set({ min_evaluators: e.target.value })} />
            </div>
          </div>

          {v.relation === 'peer' && (
            <div>
              <label className={LABEL}>ตำแหน่งใดในสาขาเดียวกันนับเป็นเพื่อนร่วมงาน</label>
              <div className="flex flex-wrap gap-1.5">
                {POSITIONS.map((p) => {
                  const on = (v.peer_positions || []).includes(p)
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => togglePeer(p)}
                      className={`rounded-full px-2.5 py-1 text-[12.5px] font-medium border transition-colors ${
                        on ? 'border-brand-400 bg-brand-50 text-brand-700' : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      {p}
                    </button>
                  )
                })}
              </div>
              <p className="text-[11.5px] text-slate-400 mt-1.5">ไม่เลือกเลย = ทุกตำแหน่งในสาขาเดียวกัน</p>
            </div>
          )}

          {err && (
            <div className="flex items-start gap-2 text-[13px] text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2.5">
              <AlertCircle size={14} className="mt-0.5 flex-none" />{err}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-xl text-[14px] font-medium text-slate-600 hover:bg-slate-50">ยกเลิก</button>
          <button
            type="button"
            disabled={busy || !v.subject_position || !Number(v.form_id)}
            onClick={save}
            className="px-4 py-2.5 rounded-xl text-[14px] font-semibold text-white bg-brand-600 hover:bg-brand-700 shadow-sm disabled:opacity-40 inline-flex items-center gap-2"
          >
            {busy && <Loader2 size={15} className="animate-spin" />} บันทึก
          </button>
        </div>
      </div>
    </div>
  )
}
